import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CardsTab } from "./CardsTab";
import { Deck } from "./Deck";
import { DeckAnalyticsModal } from "./DeckAnalyticsModal";
import { DetailModal } from "./DetailModal";
import { ImportDeckModal } from "./ImportDeckModal";
import { MyDecksTab } from "./MyDecksTab";
import { useDebounced } from "../hooks/useDebounced";
import { copyToClipboard } from "../utils/copyToClipboard";
import { useScrollHideSiteHeader } from "../hooks/useScrollHideSiteHeader";
import { buildQuery, jsonHeaders } from "../utils/api";
import {
  CARD_FILTER_URL_KEYS,
  buildCardFiltersQueryString,
  readInitialCardFilters,
} from "../utils/cardFilters";
import { groupDeckLinesByType } from "../utils/deckGrouping";

const DEBOUNCE_MS = 350;
const CARDS_PER_PAGE = 24;
/** Larger first pages while searching so likely matches are already fetched (API still paginates). */
const CARDS_PER_PAGE_SEARCH = 72;

export default function App() {
  const [options, setOptions] = useState({
    spheres: [],
    types: [],
    packs: [],
    costs: [],
  });
  const [qInput, setQInput] = useState(() => readInitialCardFilters().q);
  /** When empty, API shows all spheres; default Neutral-only matches typical deck-building focus. */
  const [selectedSpheres, setSelectedSpheres] = useState(() => readInitialCardFilters().selectedSpheres);
  const [selectedTypes, setSelectedTypes] = useState(() => readInitialCardFilters().selectedTypes);
  const [selectedCosts, setSelectedCosts] = useState(() => readInitialCardFilters().selectedCosts);
  const [selectedPacks, setSelectedPacks] = useState(() => readInitialCardFilters().selectedPacks);
  const debouncedQ = useDebounced(qInput, DEBOUNCE_MS);
  const [cards, setCards] = useState([]);
  const [listMeta, setListMeta] = useState({ page: 0, per_page: CARDS_PER_PAGE, total_count: 0, total_pages: 0 });
  const [nextPage, setNextPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mainTab, setMainTab] = useState("cards");
  const [session, setSession] = useState({
    signed_in: false,
    email: null,
    ringsdb_connected: false,
    ringsdb_export_enabled: false,
  });
  const [deck, setDeck] = useState(null);
  const [deckSummaries, setDeckSummaries] = useState([]);
  const [deckListFetched, setDeckListFetched] = useState(false);
  // const [saveAsName, setSaveAsName] = useState("");
  // const [savingCopy, setSavingCopy] = useState(false);
  // const [saveCopyMessage, setSaveCopyMessage] = useState(null);
  const [settingCurrentDeckId, setSettingCurrentDeckId] = useState(null);
  const [deletingDeckId, setDeletingDeckId] = useState(null);
  const [startingNewDeck, setStartingNewDeck] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importingDeck, setImportingDeck] = useState(false);
  const [importError, setImportError] = useState(null);
  const [exportingDeckId, setExportingDeckId] = useState(null);
  const [ringsdbFlash, setRingsdbFlash] = useState(null);
  const [deckNameDraft, setDeckNameDraft] = useState("");
  const [savingDeckMeta, setSavingDeckMeta] = useState(false);
  const [updateDeckMessage, setUpdateDeckMessage] = useState(null);
  const [deckAnalyticsOpen, setDeckAnalyticsOpen] = useState(false);
  /** Avoid resetting the name field when `deck` is replaced after add/remove (same id + name). */
  const lastSyncedDeckKeyRef = useRef(null);
  const lastSyncedDeckNameRef = useRef("");

  const qtyByCardId = useMemo(() => {
    const m = {};
    (deck?.lines || []).forEach((line) => {
      m[line.card_id] = line.quantity;
    });
    return m;
  }, [deck]);

  const deckSectionsByType = useMemo(
    () => (deck?.lines?.length ? groupDeckLinesByType(deck.lines) : []),
    [deck?.lines]
  );

  const sphereKey = useMemo(() => [...selectedSpheres].sort().join("|"), [selectedSpheres]);

  const typeKey = useMemo(() => [...selectedTypes].sort().join("|"), [selectedTypes]);
  const costKey = useMemo(() => [...selectedCosts].sort().join("|"), [selectedCosts]);
  const packKey = useMemo(() => [...selectedPacks].sort().join("|"), [selectedPacks]);

  const filterKey = useMemo(
    () => `${debouncedQ}\0${sphereKey}\0${typeKey}\0${costKey}\0${packKey}`,
    [debouncedQ, sphereKey, typeKey, costKey, packKey]
  );

  const listPageSize = debouncedQ.trim() ? CARDS_PER_PAGE_SEARCH : CARDS_PER_PAGE;

  useEffect(() => {
    if (mainTab !== "cards") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [filterKey, mainTab]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const usp = new URLSearchParams(window.location.search);
    CARD_FILTER_URL_KEYS.forEach((k) => usp.delete(k));
    const fragment = buildCardFiltersQueryString({
      q: qInput,
      selectedSpheres,
      selectedTypes,
      selectedCosts,
      selectedPacks,
    });
    if (fragment) {
      new URLSearchParams(fragment).forEach((v, k) => {
        usp.set(k, v);
      });
    }
    const qs = usp.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    const cur = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== cur) window.history.replaceState(null, "", next);
  }, [qInput, selectedSpheres, selectedTypes, selectedCosts, selectedPacks]);

  const loadMoreInFlight = useRef(false);
  const listFetchGen = useRef(0);
  const loadMoreRef = useRef(() => {});
  const scrollSentinelRef = useRef(null);

  useEffect(() => {
    fetch("/api/cards/filter_options")
      .then((r) => r.json())
      .then(setOptions)
      .catch(() => {});
  }, []);

  const refreshSession = useCallback(() => {
    fetch("/api/session")
      .then((r) => r.json())
      .then(setSession)
      .catch(() => setSession({ signed_in: false, email: null }));
  }, []);

  const refreshDeck = useCallback(() => {
    return fetch("/api/deck")
      .then((r) => {
        if (r.status === 401) {
          setDeck(null);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) setDeck(data);
      })
      .catch(() => setDeck(null));
  }, []);

  const refreshDeckList = useCallback(() => {
    return fetch("/api/decks")
      .then((r) => {
        if (r.status === 401) return null;
        return r.json();
      })
      .then((data) => {
        if (data?.decks) setDeckSummaries(data.decks);
      })
      .catch(() => {})
      .finally(() => {
        setDeckListFetched(true);
      });
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const usp = new URLSearchParams(window.location.search);
    if (usp.get("ringsdb") !== "connected") return;

    setMainTab("my-decks");
    setRingsdbFlash("RingsDB account connected. You can export decks now.");
    refreshSession();
    usp.delete("ringsdb");
    const qs = usp.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", next);
  }, [refreshSession]);

  useEffect(() => {
    if (session.signed_in) {
      refreshDeck();
      refreshDeckList();
    } else {
      setDeck(null);
      setDeckSummaries([]);
      setDeckListFetched(false);
    }
  }, [session.signed_in, refreshDeck, refreshDeckList]);

  useEffect(() => {
    if (session.signed_in && mainTab === "my-decks") refreshDeckList();
  }, [mainTab, session.signed_in, refreshDeckList]);

  // useEffect(() => {
  //   if (deck?.name != null) setSaveAsName(deck.name);
  // }, [deck?.id, deck?.name]);

  useEffect(() => {
    if (!deck) {
      lastSyncedDeckKeyRef.current = null;
      lastSyncedDeckNameRef.current = "";
      return;
    }

    const idKey = String(deck.id);
    const rawName = deck.name ?? "";

    if (lastSyncedDeckKeyRef.current !== idKey) {
      setDeckNameDraft(rawName);
      lastSyncedDeckKeyRef.current = idKey;
      lastSyncedDeckNameRef.current = rawName;
      return;
    }

    if (rawName !== lastSyncedDeckNameRef.current) {
      setDeckNameDraft(rawName);
      lastSyncedDeckNameRef.current = rawName;
    }
  }, [deck]);

  const addToDeck = useCallback(
    async (cardId) => {
      if (!session.signed_in) return;
      const r = await fetch("/api/deck/add", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ card_id: cardId }),
      });
      if (r.ok) setDeck(await r.json());
      else if (r.status === 401) refreshSession();
    },
    [session.signed_in, refreshSession]
  );

  const removeFromDeck = useCallback(
    async (cardId) => {
      if (!session.signed_in) return;
      const r = await fetch("/api/deck/remove", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ card_id: cardId }),
      });
      if (r.ok) setDeck(await r.json());
      else if (r.status === 401) refreshSession();
    },
    [session.signed_in, refreshSession]
  );

  const updateCurrentDeck = useCallback(async () => {
    if (!session.signed_in || !deck) return;
    const trimmed = deckNameDraft.trim();
    const serverName = (deck.name ?? "").trim();
    if (trimmed === serverName) {
      setUpdateDeckMessage({ kind: "ok", text: "No changes to save." });
      return;
    }
    setSavingDeckMeta(true);
    setUpdateDeckMessage(null);
    try {
      const r = await fetch("/api/deck", {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ name: trimmed }),
      });
      if (r.status === 401) {
        refreshSession();
        return;
      }
      if (r.ok) {
        const data = await r.json();
        setDeck(data);
        setDeckNameDraft(data.name);
        setUpdateDeckMessage({ kind: "ok", text: "Deck updated." });
        refreshDeckList();
      } else if (r.status === 422) {
        const body = await r.json().catch(() => ({}));
        setUpdateDeckMessage({ kind: "err", text: body.error || "Could not update deck." });
      } else {
        setUpdateDeckMessage({ kind: "err", text: "Could not update deck." });
      }
    } catch {
      setUpdateDeckMessage({ kind: "err", text: "Could not update deck." });
    } finally {
      setSavingDeckMeta(false);
    }
  }, [session.signed_in, deck, deckNameDraft, refreshSession, refreshDeckList]);

  // const saveDeckCopy = useCallback(async () => {
  //   if (!session.signed_in || !deck) return;
  //   setSavingCopy(true);
  //   setSaveCopyMessage(null);
  //   try {
  //     const r = await fetch("/api/deck/save", {
  //       method: "POST",
  //       headers: jsonHeaders(),
  //       body: JSON.stringify({ name: saveAsName }),
  //     });
  //     if (r.status === 401) {
  //       refreshSession();
  //       return;
  //     }
  //     if (r.ok) {
  //       const payload = await r.json();
  //       setSaveCopyMessage({ kind: "ok", text: `Saved as “${payload.name}”.` });
  //       refreshDeckList();
  //     } else {
  //       setSaveCopyMessage({ kind: "err", text: "Could not save deck." });
  //     }
  //   } catch {
  //     setSaveCopyMessage({ kind: "err", text: "Could not save deck." });
  //   } finally {
  //     setSavingCopy(false);
  //   }
  // }, [session.signed_in, deck, saveAsName, refreshSession, refreshDeckList]);

  const activateDeck = useCallback(
    async (deckId) => {
      if (!session.signed_in) return;
      setSettingCurrentDeckId(deckId);
      try {
        const r = await fetch(`/api/decks/${deckId}/set_current`, {
          method: "POST",
          headers: jsonHeaders(),
        });
        if (r.status === 401) {
          refreshSession();
          return;
        }
        if (r.ok) {
          await refreshDeck();
          await refreshDeckList();
        }
      } catch {
        /* ignore */
      } finally {
        setSettingCurrentDeckId(null);
      }
    },
    [session.signed_in, refreshSession, refreshDeck, refreshDeckList]
  );

  const deleteDeck = useCallback(
    async (deckId, deckName) => {
      if (!session.signed_in) return;
      if (!window.confirm(`Delete “${deckName}”? This cannot be undone.`)) return;
      setDeletingDeckId(deckId);
      try {
        const r = await fetch(`/api/decks/${deckId}`, {
          method: "DELETE",
          headers: jsonHeaders(),
        });
        if (r.status === 401) {
          refreshSession();
          return;
        }
        if (r.ok) {
          await refreshDeck();
          await refreshDeckList();
        }
      } catch {
        /* ignore */
      } finally {
        setDeletingDeckId(null);
      }
    },
    [session.signed_in, refreshSession, refreshDeck, refreshDeckList]
  );

  const startNewDeck = useCallback(async () => {
    if (!session.signed_in) return;

    const cardCount = deck?.total_cards ?? 0;
    if (
      cardCount > 0 &&
      !window.confirm(
        "Start a new empty deck? Your current deck will stay in My decks with all its cards."
      )
    ) {
      return;
    }

    setStartingNewDeck(true);
    try {
      const r = await fetch("/api/decks", {
        method: "POST",
        headers: jsonHeaders(),
      });
      if (r.status === 401) {
        refreshSession();
        return;
      }
      if (r.ok) {
        await refreshDeck();
        await refreshDeckList();
        setMainTab("cards");
      }
    } catch {
      /* ignore */
    } finally {
      setStartingNewDeck(false);
    }
  }, [session.signed_in, deck?.total_cards, refreshSession, refreshDeck, refreshDeckList]);

  const connectRingsdb = useCallback((returnTo) => {
    const target = returnTo || window.location.pathname + window.location.search;
    window.location.href = `/ringsdb/connect?return_to=${encodeURIComponent(target)}`;
  }, []);

  const importDeckFromRingsdb = useCallback(
    async (url) => {
      if (!session.signed_in) return;
      setImportingDeck(true);
      setImportError(null);
      try {
        const r = await fetch("/api/ringsdb/import", {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ url }),
        });
        if (r.status === 401) {
          refreshSession();
          return;
        }
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          setImportError(body.error || "Could not import deck.");
          return;
        }

        setImportModalOpen(false);
        await refreshDeck();
        await refreshDeckList();
        setMainTab("deck");

        const warnings = body.warnings || [];
        if (warnings.length > 0) {
          setRingsdbFlash(
            `Imported “${body.deck?.name || "deck"}”. ${warnings.length} card(s) were skipped (not in the local database).`
          );
        } else {
          setRingsdbFlash(`Imported “${body.deck?.name || "deck"}” as your current deck.`);
        }
      } catch {
        setImportError("Could not import deck.");
      } finally {
        setImportingDeck(false);
      }
    },
    [session.signed_in, refreshSession, refreshDeck, refreshDeckList]
  );

  const exportDeckToRingsdb = useCallback(
    async (deckId) => {
      if (!session.signed_in) return;

      if (!session.ringsdb_connected) {
        connectRingsdb();
        return;
      }

      setExportingDeckId(deckId);
      try {
        const r = await fetch("/api/ringsdb/export", {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ deck_id: deckId }),
        });
        if (r.status === 401) {
          const body = await r.json().catch(() => ({}));
          if (body.connect_url) {
            window.location.href = body.connect_url;
            return;
          }
          refreshSession();
          return;
        }
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          window.alert(body.error || "Could not export deck to RingsDB.");
          return;
        }

        if (body.url) {
          window.open(body.url, "_blank", "noopener,noreferrer");
          try {
            await copyToClipboard(body.url);
            setRingsdbFlash("Deck exported to RingsDB. Link copied to clipboard and opened in a new tab.");
          } catch {
            setRingsdbFlash("Deck exported to RingsDB and opened in a new tab.");
          }
        }
      } catch {
        window.alert("Could not export deck to RingsDB.");
      } finally {
        setExportingDeckId(null);
      }
    },
    [session.signed_in, session.ringsdb_connected, connectRingsdb, refreshSession]
  );

  useEffect(() => {
    let cancelled = false;
    listFetchGen.current += 1;
    loadMoreInFlight.current = false;
    setInitialLoading(true);
    setCards([]);
    setNextPage(1);
    setListMeta({ page: 0, per_page: listPageSize, total_count: 0, total_pages: 0 });

    const qs = buildQuery({
      q: debouncedQ,
      ...(selectedSpheres.length > 0 ? { spheres: selectedSpheres } : {}),
      ...(selectedTypes.length > 0 ? { type: selectedTypes } : {}),
      ...(selectedCosts.length > 0 ? { cost: selectedCosts } : {}),
      ...(selectedPacks.length > 0 ? { pack: selectedPacks } : {}),
      page: 1,
      per_page: listPageSize,
    });

    fetch(`/api/cards?${qs}`)
      .then((r) => r.json())
      .then((payload) => {
        if (cancelled) return;
        setCards(payload.cards || []);
        setListMeta(payload.meta || { page: 1, per_page: listPageSize, total_count: 0, total_pages: 0 });
        setNextPage(2);
      })
      .catch(() => {
        if (cancelled) return;
        setCards([]);
        setListMeta({ page: 0, per_page: listPageSize, total_count: 0, total_pages: 0 });
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filterKey, listPageSize]);

  const loadMore = useCallback(() => {
    if (initialLoading || loadingMore || loadMoreInFlight.current) return;
    const { total_pages } = listMeta;
    if (!total_pages || nextPage > total_pages) return;

    const gen = listFetchGen.current;
    loadMoreInFlight.current = true;
    setLoadingMore(true);
    const qs = buildQuery({
      q: debouncedQ,
      ...(selectedSpheres.length > 0 ? { spheres: selectedSpheres } : {}),
      ...(selectedTypes.length > 0 ? { type: selectedTypes } : {}),
      ...(selectedCosts.length > 0 ? { cost: selectedCosts } : {}),
      ...(selectedPacks.length > 0 ? { pack: selectedPacks } : {}),
      page: nextPage,
      per_page: listPageSize,
    });

    fetch(`/api/cards?${qs}`)
      .then((r) => r.json())
      .then((payload) => {
        if (gen !== listFetchGen.current) return;
        const more = payload.cards || [];
        setCards((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const merged = [...prev];
          for (const c of more) {
            if (!seen.has(c.id)) {
              seen.add(c.id);
              merged.push(c);
            }
          }
          return merged;
        });
        if (payload.meta) setListMeta(payload.meta);
        setNextPage((p) => p + 1);
      })
      .catch(() => {})
      .finally(() => {
        loadMoreInFlight.current = false;
        if (gen === listFetchGen.current) setLoadingMore(false);
      });
  }, [
    initialLoading,
    loadingMore,
    nextPage,
    listMeta.total_pages,
    debouncedQ,
    selectedSpheres,
    selectedTypes,
    selectedCosts,
    selectedPacks,
    listPageSize,
  ]);

  loadMoreRef.current = loadMore;

  const totalCount = listMeta.total_count ?? 0;
  const totalPages = listMeta.total_pages ?? 0;
  const hasMore = totalPages > 0 && nextPage <= totalPages;

  useEffect(() => {
    if (mainTab !== "cards") return;
    const el = scrollSentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root: null, rootMargin: "800px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [filterKey, mainTab]);

  /** Prefetch when the sentinel is already in/near the viewport (IO can miss after layout/filter changes). */
  useEffect(() => {
    if (mainTab !== "cards") return;
    if (initialLoading || loadingMore) return;
    if (!hasMore) return;

    let cancelled = false;
    const tick = () => {
      if (cancelled || loadMoreInFlight.current) return;
      const el = scrollSentinelRef.current;
      if (!el) return;
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      const rect = el.getBoundingClientRect();
      if (rect.top <= vh + 800) loadMoreRef.current();
    };

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(tick);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [mainTab, cards.length, initialLoading, loadingMore, hasMore, filterKey]);

  useEffect(() => {
    if (detailId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/cards/${detailId}`)
      .then((r) => r.json())
      .then((c) => {
        if (!cancelled) setDetail(c);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [detailId]);

  const toggleListFilter = useCallback((setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return [...next];
    });
  }, []);

  const toggleType = useCallback(
    (name) => toggleListFilter(setSelectedTypes, name),
    [toggleListFilter]
  );
  const toggleCost = useCallback(
    (name) => toggleListFilter(setSelectedCosts, name),
    [toggleListFilter]
  );
  const togglePack = useCallback(
    (name) => toggleListFilter(setSelectedPacks, name),
    [toggleListFilter]
  );

  const toggleSphere = useCallback((name) => {
    setSelectedSpheres((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return [...next];
    });
  }, []);

  const signedIn = Boolean(session.signed_in);

  useScrollHideSiteHeader();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="sticky top-[var(--auth-toolbar-height,3.5rem)] z-40 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl gap-2 px-4 pt-2" role="tablist" aria-label="Main navigation">
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "cards"}
            onClick={() => setMainTab("cards")}
            className={[
              "rounded-t-lg border border-b-0 px-4 py-2 text-sm font-medium transition",
              mainTab === "cards"
                ? "border-slate-600 bg-slate-950 text-amber-100"
                : "border-transparent bg-transparent text-slate-500 hover:text-slate-300",
            ].join(" ")}
          >
            Cards
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "deck"}
            onClick={() => setMainTab("deck")}
            className={[
              "rounded-t-lg border border-b-0 px-4 py-2 text-sm font-medium transition",
              mainTab === "deck"
                ? "border-slate-600 bg-slate-950 text-amber-100"
                : "border-transparent bg-transparent text-slate-500 hover:text-slate-300",
            ].join(" ")}
          >
            Current deck
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mainTab === "my-decks"}
            onClick={() => setMainTab("my-decks")}
            className={[
              "rounded-t-lg border border-b-0 px-4 py-2 text-sm font-medium transition",
              mainTab === "my-decks"
                ? "border-slate-600 bg-slate-950 text-amber-100"
                : "border-transparent bg-transparent text-slate-500 hover:text-slate-300",
            ].join(" ")}
          >
            My decks
          </button>
        </div>
      </div>

      {mainTab === "cards" && (
        <CardsTab
          options={options}
          qInput={qInput}
          onQInputChange={setQInput}
          selectedSpheres={selectedSpheres}
          onToggleSphere={toggleSphere}
          selectedTypes={selectedTypes}
          onToggleType={toggleType}
          selectedCosts={selectedCosts}
          onToggleCost={toggleCost}
          selectedPacks={selectedPacks}
          onTogglePack={togglePack}
          cards={cards}
          initialLoading={initialLoading}
          loadingMore={loadingMore}
          totalCount={totalCount}
          totalPages={totalPages}
          hasMore={hasMore}
          signedIn={signedIn}
          qtyByCardId={qtyByCardId}
          onOpenCard={setDetailId}
          onDeckAdd={addToDeck}
          onDeckRemove={removeFromDeck}
          scrollSentinelRef={scrollSentinelRef}
        />
      )}

      {mainTab === "deck" && (
        <Deck
          signedIn={signedIn}
          deck={deck}
          deckSectionsByType={deckSectionsByType}
          deckNameDraft={deckNameDraft}
          onDeckNameDraftChange={setDeckNameDraft}
          // saveAsName={saveAsName}
          // onSaveAsNameChange={setSaveAsName}
          savingDeckMeta={savingDeckMeta}
          updateDeckMessage={updateDeckMessage}
          onSaveDeckName={updateCurrentDeck}
          // savingCopy={savingCopy}
          // saveCopyMessage={saveCopyMessage}
          // onSaveCopy={saveDeckCopy}
          onGoToCards={() => setMainTab("cards")}
          onOpenCard={setDetailId}
          onDeckAdd={addToDeck}
          onDeckRemove={removeFromDeck}
          onAnalytics={() => setDeckAnalyticsOpen(true)}
        />
      )}

      {mainTab === "my-decks" && (
        <MyDecksTab
          signedIn={signedIn}
          deckListFetched={deckListFetched}
          deckSummaries={deckSummaries}
          settingCurrentDeckId={settingCurrentDeckId}
          deletingDeckId={deletingDeckId}
          startingNewDeck={startingNewDeck}
          importingDeck={importingDeck}
          exportingDeckId={exportingDeckId}
          ringsdbConnected={session.ringsdb_connected}
          ringsdbExportEnabled={session.ringsdb_export_enabled}
          onStartNewDeck={startNewDeck}
          onImportDeck={() => {
            setImportError(null);
            setImportModalOpen(true);
          }}
          onActivateDeck={activateDeck}
          onDeleteDeck={deleteDeck}
          onExportDeck={exportDeckToRingsdb}
          onConnectRingsdb={() => connectRingsdb()}
        />
      )}

      {ringsdbFlash && (
        <div className="fixed bottom-4 left-1/2 z-50 max-w-md -translate-x-1/2 rounded-lg border border-amber-700/60 bg-amber-950/95 px-4 py-3 text-sm text-amber-100 shadow-lg">
          <div className="flex items-start justify-between gap-3">
            <p>{ringsdbFlash}</p>
            <button
              type="button"
              onClick={() => setRingsdbFlash(null)}
              className="shrink-0 text-amber-300/80 hover:text-amber-100"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <ImportDeckModal
        open={importModalOpen}
        busy={importingDeck}
        error={importError}
        onClose={() => {
          if (!importingDeck) setImportModalOpen(false);
        }}
        onSubmit={importDeckFromRingsdb}
      />

      <DetailModal
        card={detail}
        onClose={() => setDetailId(null)}
        signedIn={signedIn}
        deckQuantity={detail ? (qtyByCardId[detail.id] ?? 0) : 0}
        onDeckAdd={detail ? () => addToDeck(detail.id) : undefined}
        onDeckRemove={detail ? () => removeFromDeck(detail.id) : undefined}
      />

      {deckAnalyticsOpen && deck && (
        <DeckAnalyticsModal
          deckName={deck.name}
          lines={deck.lines}
          onClose={() => setDeckAnalyticsOpen(false)}
        />
      )}

      <footer className="border-b border-slate-800 bg-slate-900/80 px-4 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-100">Card browser</p>
            <p className="text-sm text-slate-400">Lord of the Rings LCG</p>
          </div>
          <p className="text-xs text-slate-500">Data from RingsDB · personal use</p>
        </div>
      </footer>
    </div>
  );
}
